import React, { useEffect, useRef, useState } from "react";
import {
  FaBookOpen,
  FaUsers,
  FaRupeeSign,
  FaCheckCircle,
  FaArrowRight,
  FaStar,
  FaChalkboardTeacher,
  FaFlask,
  FaLandmark,
  FaChartLine,
  FaGraduationCap,
  FaBriefcase,
  FaRegClock,
  FaUniversity,
  FaAtom,
  FaFileInvoiceDollar,
} from "react-icons/fa";
import { MdOutlineSubject } from "react-icons/md";

const CourseDetailsSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeCourse, setActiveCourse] = useState("bcom");
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const courses = {
    bcom: {
      name: "Bachelor of Commerce",
      shortName: "B.Com",
      icon: <FaChartLine className="text-2xl" />,
      duration: "3 Years",
      eligibility: "10+2 with Commerce/Mathematics",
      fees: "As per college norms",
      seats: "120",
      description:
        "Bachelor of Commerce is a career-oriented program focused on accounting, finance, taxation, business management, and economics.",
      syllabus: [
        "Financial Accounting",
        "Business Economics",
        "Business Mathematics",
        "Corporate Accounting",
        "Cost Accounting",
        "Income Tax Law",
        "Auditing",
        "Business Statistics",
        "Financial Management",
        "Marketing Management",
        "Business Law",
        "Banking",
      ],
      career: [
        "Chartered Accountant",
        "Company Secretary",
        "Financial Analyst",
        "Banking Professional",
        "Tax Consultant",
        "Business Consultant",
        "Accounts Manager",
        "Entrepreneur",
      ],
      features: [
        "Commerce-focused curriculum",
        "Accounting knowledge",
        "Career guidance",
        "Placement assistance",
        "Business learning",
      ],
    },

    bsc: {
      name: "Bachelor of Science",
      shortName: "B.Sc",
      icon: <FaFlask className="text-2xl" />,
      duration: "3 Years",
      eligibility: "10+2 with Science",
      fees: "As per college norms",
      seats: "90",
      description:
        "Bachelor of Science offers strong academic learning in science subjects with practical exposure and laboratory-based education.",
      syllabus: [
        "Physics",
        "Chemistry",
        "Mathematics",
        "Botany",
        "Zoology",
        "Biology",
        "Environmental Science",
        "Computer Applications",
        "Research Methodology",
      ],
      career: [
        "Research Assistant",
        "Lab Technician",
        "Quality Analyst",
        "Teacher",
        "Pharmaceutical Professional",
        "Environmental Consultant",
        "Data Analyst",
        "Higher Studies",
      ],
      features: [
        "Modern laboratories",
        "Practical training",
        "Research projects",
        "Field learning",
        "Career counseling",
      ],
    },

    ba: {
      name: "Bachelor of Arts",
      shortName: "B.A",
      icon: <FaLandmark className="text-2xl" />,
      duration: "3 Years",
      eligibility: "10+2 in any stream",
      fees: "As per college norms",
      seats: "180",
      description:
        "Bachelor of Arts provides a broad academic foundation in humanities, languages, and social science subjects.",
      syllabus: [
        "Hindi",
        "English",
        "Sanskrit",
        "History",
        "Political Science",
        "Sociology",
        "Economics",
        "Education",
        "Home Science",
        "Geography",
      ],
      career: [
        "Civil Services",
        "Teacher",
        "Journalist",
        "Social Worker",
        "Public Relations Officer",
        "Content Writer",
        "Law",
        "Management Professional",
      ],
      features: [
        "Multiple subject choices",
        "Personality development",
        "Debate activities",
        "Career guidance",
        "Competitive exam support",
      ],
    },

    mcom: {
      name: "Master of Commerce",
      shortName: "M.Com",
      icon: <FaFileInvoiceDollar className="text-2xl" />,
      duration: "2 Years",
      eligibility: "B.Com or equivalent degree",
      fees: "As per college norms",
      seats: "40",
      description:
        "Master of Commerce is a postgraduate program that develops advanced knowledge in commerce, finance, accounting, taxation, and management.",
      syllabus: [
        "Advanced Accounting",
        "Corporate Finance",
        "Business Economics",
        "Taxation",
        "Banking",
        "Marketing Management",
        "Business Law",
        "Research Methodology",
        "Financial Management",
        "Organizational Behaviour",
      ],
      career: [
        "Finance Manager",
        "Accountant",
        "Tax Consultant",
        "Banking Officer",
        "Assistant Professor",
        "Business Analyst",
        "Corporate Executive",
        "Research Scholar",
      ],
      features: [
        "Advanced commerce study",
        "Finance-based learning",
        "Research orientation",
        "Professional growth",
        "Career-focused curriculum",
      ],
    },

    msc: {
      name: "Master of Science",
      shortName: "M.Sc",
      icon: <FaAtom className="text-2xl" />,
      duration: "2 Years",
      eligibility: "B.Sc in relevant subject",
      fees: "As per college norms",
      seats: "40",
      description:
        "Master of Science offers advanced scientific education with deeper subject specialization, practical learning, and research exposure.",
      syllabus: [
        "Physics",
        "Chemistry",
        "Mathematics",
        "Botany",
        "Zoology",
        "Biology",
        "Research Methodology",
        "Advanced Practical",
        "Dissertation Work",
      ],
      career: [
        "Research Scientist",
        "Lecturer",
        "Lab Analyst",
        "Academic Researcher",
        "Pharmaceutical Sector",
        "Scientific Officer",
        "Quality Control Executive",
        "PhD Scholar",
      ],
      features: [
        "Advanced practical learning",
        "Research projects",
        "Laboratory training",
        "Academic development",
        "Higher study support",
      ],
    },

    ma: {
      name: "Master of Arts",
      shortName: "M.A",
      icon: <FaUniversity className="text-2xl" />,
      duration: "2 Years",
      eligibility: "Graduation in relevant subject",
      fees: "As per college norms",
      seats: "60",
      description:
        "Master of Arts is a postgraduate humanities program designed for advanced subject knowledge, academic growth, and research-based learning.",
      syllabus: [
        "Hindi",
        "English",
        "Sanskrit",
        "History",
        "Political Science",
        "Sociology",
        "Economics",
        "Education",
        "Home Science",
        "Geography",
        "Research Methodology",
      ],
      career: [
        "Lecturer",
        "Research Scholar",
        "Civil Services",
        "Content Writer",
        "Social Worker",
        "Public Administrator",
        "Journalist",
        "Academic Professional",
      ],
      features: [
        "Advanced subject knowledge",
        "Research orientation",
        "Academic guidance",
        "Competitive exam support",
        "Higher education pathway",
      ],
    },
  };

  const tabs = [
    { id: "bcom", name: "B.Com", icon: <FaChartLine /> },
    { id: "bsc", name: "B.Sc", icon: <FaFlask /> },
    { id: "ba", name: "B.A", icon: <FaLandmark /> },
    { id: "mcom", name: "M.Com", icon: <FaFileInvoiceDollar /> },
    { id: "msc", name: "M.Sc", icon: <FaAtom /> },
    { id: "ma", name: "M.A", icon: <FaUniversity /> },
  ];

  const currentCourse = courses[activeCourse];

  return (
    <section
      ref={sectionRef}
      className="w-full bg-gradient-to-br from-yellow-50 via-white to-blue-50 py-16"
    >
      <div className="w-full px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Decorative Top */}
          <div className="flex justify-center gap-3 mb-6">
            <div className="w-12 h-1 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-full"></div>
            <div className="w-8 h-1 bg-gradient-to-r from-orange-400 to-orange-500 rounded-full"></div>
            <div className="w-6 h-1 bg-gradient-to-r from-blue-400 to-blue-500 rounded-full"></div>
          </div>

          {/* Heading */}
          <div className="text-center mb-10">
            <h2 className="text-4xl md:text-5xl font-bold mb-3">
              <span className="bg-gradient-to-r from-yellow-500 via-orange-500 to-blue-500 bg-clip-text text-transparent">
                Course Details
              </span>
            </h2>

            <div className="w-24 h-1 bg-gradient-to-r from-yellow-400 via-orange-500 to-blue-500 rounded-full mx-auto"></div>

            <p className="text-gray-600 text-sm mt-4">
              Explore our academic programs in detail
            </p>
          </div>

          {/* Scrollable Tab Buttons */}
          <div
            className={`mb-10 transition-all duration-500 ${
              isVisible
                ? "translate-y-0 opacity-100"
                : "translate-y-10 opacity-0"
            }`}
          >
            <div className="flex gap-4 overflow-x-auto pb-3 custom-scroll-x justify-start md:justify-center">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveCourse(tab.id)}
                  className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm transition-all duration-300 whitespace-nowrap ${
                    activeCourse === tab.id
                      ? "bg-gradient-to-r from-yellow-400 via-orange-500 to-blue-500 text-white shadow-lg scale-105"
                      : "bg-white text-gray-600 hover:shadow-md hover:scale-105"
                  }`}
                >
                  {tab.icon}
                  {tab.name}
                </button>
              ))}
            </div>
          </div>

          {/* Course Content */}
          <div
            className={`bg-white rounded-2xl shadow-xl overflow-hidden transition-all duration-500 ${
              isVisible
                ? "translate-y-0 opacity-100"
                : "translate-y-10 opacity-0"
            }`}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-yellow-400 via-orange-500 to-blue-500 p-6 text-white">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 p-3 rounded-xl">
                  {currentCourse.icon}
                </div>

                <div>
                  <h3 className="text-2xl font-bold">{currentCourse.name}</h3>
                  <p className="text-white/90 text-sm mt-1">
                    {currentCourse.shortName} Program
                  </p>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="p-6">
              {/* Quick Info Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gray-50 rounded-xl p-3 text-center">
                  <FaRegClock className="text-yellow-500 text-xl mx-auto mb-1" />
                  <p className="text-gray-600 text-xs">Duration</p>
                  <p className="text-gray-800 font-semibold text-sm">
                    {currentCourse.duration}
                  </p>
                </div>

                <div className="bg-gray-50 rounded-xl p-3 text-center">
                  <FaGraduationCap className="text-yellow-500 text-xl mx-auto mb-1" />
                  <p className="text-gray-600 text-xs">Eligibility</p>
                  <p className="text-gray-800 font-semibold text-xs">
                    {currentCourse.eligibility}
                  </p>
                </div>

                <div className="bg-gray-50 rounded-xl p-3 text-center">
                  <FaRupeeSign className="text-yellow-500 text-xl mx-auto mb-1" />
                  <p className="text-gray-600 text-xs">Fees</p>
                  <p className="text-gray-800 font-semibold text-xs">
                    {currentCourse.fees}
                  </p>
                </div>

                <div className="bg-gray-50 rounded-xl p-3 text-center">
                  <FaUsers className="text-yellow-500 text-xl mx-auto mb-1" />
                  <p className="text-gray-600 text-xs">Seats</p>
                  <p className="text-gray-800 font-semibold text-sm">
                    {currentCourse.seats}
                  </p>
                </div>
              </div>

              {/* Description */}
              <div className="mb-6">
                <h4 className="font-bold text-gray-800 text-lg mb-2 flex items-center gap-2">
                  <FaBookOpen className="text-yellow-500" />
                  About the Course
                </h4>

                <p className="text-gray-600 text-sm leading-relaxed">
                  {currentCourse.description}
                </p>
              </div>

              {/* Syllabus and Career Grid */}
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                {/* Syllabus */}
                <div className="bg-yellow-50 rounded-xl p-4">
                  <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                    <MdOutlineSubject className="text-yellow-500 text-lg" />
                    Subjects / Syllabus Highlights
                  </h4>

                  <div className="max-h-48 overflow-y-auto pr-2 custom-scroll">
                    <div className="grid grid-cols-1 gap-2">
                      {currentCourse.syllabus.map((subject, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <FaCheckCircle className="text-green-500 text-xs shrink-0" />
                          <span className="text-gray-600 text-xs">
                            {subject}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Career Opportunities */}
                <div className="bg-blue-50 rounded-xl p-4">
                  <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                    <FaBriefcase className="text-blue-500" />
                    Career Opportunities
                  </h4>

                  <div className="max-h-48 overflow-y-auto pr-2 custom-scroll">
                    <div className="grid grid-cols-1 gap-2">
                      {currentCourse.career.map((career, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <FaStar className="text-yellow-500 text-xs shrink-0" />
                          <span className="text-gray-600 text-xs">
                            {career}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Features */}
              <div className="mb-6">
                <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <FaChalkboardTeacher className="text-orange-500" />
                  Program Features
                </h4>

                <div className="flex flex-wrap gap-2">
                  {currentCourse.features.map((feature, idx) => (
                    <span
                      key={idx}
                      className="bg-gray-100 text-gray-700 text-xs px-3 py-1 rounded-full"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              </div>

              {/* Apply Button */}
              <div className="text-center pt-4 border-t border-gray-100">
                <button className="bg-gradient-to-r from-yellow-400 via-orange-500 to-blue-500 text-white px-6 py-2.5 rounded-xl font-semibold text-sm hover:shadow-xl transition-all duration-300 hover:scale-105 flex items-center gap-2 mx-auto">
                  Apply Now for {currentCourse.shortName}
                  <FaArrowRight className="text-sm" />
                </button>
              </div>
            </div>
          </div>

          {/* Bottom Decorative */}
          <div className="mt-10 flex justify-center gap-2">
            <div className="w-2 h-2 bg-yellow-400 rounded-full animate-bounce"></div>
            <div
              className="w-2 h-2 bg-orange-500 rounded-full animate-bounce"
              style={{ animationDelay: "0.2s" }}
            ></div>
            <div
              className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
              style={{ animationDelay: "0.4s" }}
            ></div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes bounce {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-8px);
          }
        }

        .animate-bounce {
          animation: bounce 1s ease-in-out infinite;
        }

        .custom-scroll::-webkit-scrollbar {
          width: 4px;
        }

        .custom-scroll::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 20px;
        }

        .custom-scroll::-webkit-scrollbar-thumb {
          background: #f59e0b;
          border-radius: 20px;
        }

        .custom-scroll-x::-webkit-scrollbar {
          height: 4px;
        }

        .custom-scroll-x::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 20px;
        }

        .custom-scroll-x::-webkit-scrollbar-thumb {
          background: #f59e0b;
          border-radius: 20px;
        }
      `}</style>
    </section>
  );
};

export default CourseDetailsSection;